<!-- Subscription Required Banner -->
@if (needsSubscription()) {
  <div class="alert alert-primary d-flex align-items-center mb-6 p-5">
    <i class="fa fa-crown text-primary fs-2 me-4"></i>
    <div class="flex-grow-1">
      <h5 class="mb-1 fw-bold">{{ 'companyProfile.subscription.requiredTitle' | translate }}</h5>
      <p class="mb-0">{{ 'companyProfile.subscription.requiredMembershipDescription' | translate }}</p>
    </div>
    <a routerLink="/company-profile" [queryParams]="{tab: 'information'}" class="btn btn-primary btn-sm">
      <i class="fa fa-arrow-right me-1"></i>
      {{ 'companyProfile.subscription.goToOverview' | translate }}
    </a>
  </div>
}

<div class="d-flex flex-wrap flex-stack mb-6">
  <h3 class="fw-bold my-2">{{ 'companyProfile.membershipPlans.title' | translate }}</h3>
  <div class="d-flex flex-wrap my-2 gap-2">
    @if (memberships.length > 0) {
      <app-button
        [variant]="'outline'"
        [label]="'companyProfile.viewMembers.export' | translate"
        [iconClass]="'fa fa-download'"
        (clicked)="openExportModal()"
      />
    }
    <app-button
      [variant]="'primary'"
      [label]="'companyProfile.membershipPlans.createPlan' | translate"
      [iconClass]="'fa fa-plus'"
      [disabled]="needsSubscription()"
      (clicked)="openAddPlanModal()"
    />
  </div>
</div>

@if (loading) {
  <div class="card mb-9">
    <div class="card-body text-center py-10">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">{{ 'companyProfile.membershipPlans.loading' | translate }}</span>
      </div>
    </div>
  </div>
} @else {
  <!-- Dashboard Stats -->
  @if (totalMembers > 0 || plans.length > 0) {
    <div class="row g-5 g-xl-8 mb-9">
      <!-- Total Members -->
      <div class="col-xl-3 col-md-6">
        <div class="card stat-card h-100">
          <div class="card-body">
            <div class="d-flex align-items-center">
              <div class="stat-icon bg-light-primary">
                <i class="fa fa-users text-primary fs-2"></i>
              </div>
              <div class="ms-4">
                <div class="fs-2hx fw-bold text-dark">{{ totalMembers }}</div>
                <div class="text-muted fw-semibold">{{ 'companyProfile.membershipPlans.stats.totalMembers' | translate }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Active Members -->
      <div class="col-xl-3 col-md-6">
        <div class="card stat-card h-100">
          <div class="card-body">
            <div class="d-flex align-items-center">
              <div class="stat-icon bg-light-success">
                <i class="fa fa-check-circle text-success fs-2"></i>
              </div>
              <div class="ms-4">
                <div class="fs-2hx fw-bold text-dark">{{ activeMembers }}</div>
                <div class="text-muted fw-semibold">{{ 'companyProfile.membershipPlans.stats.activeMembers' | translate }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- New This Month -->
      <div class="col-xl-3 col-md-6">
        <div class="card stat-card h-100">
          <div class="card-body">
            <div class="d-flex align-items-center">
              <div class="stat-icon bg-light-info">
                <i class="fa fa-user-plus text-info fs-2"></i>
              </div>
              <div class="ms-4">
                <div class="fs-2hx fw-bold text-dark">{{ newMembersThisMonth }}</div>
                <div class="text-muted fw-semibold">{{ 'companyProfile.membershipPlans.stats.newThisMonth' | translate }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Growth -->
      <div class="col-xl-3 col-md-6">
        <div class="card stat-card h-100">
          <div class="card-body">
            <div class="d-flex align-items-center">
              <div class="stat-icon" [class.bg-light-success]="growthPercentage >= 0" [class.bg-light-danger]="growthPercentage < 0">
                <i class="fa fs-2"
                   [class.fa-arrow-up]="growthPercentage >= 0"
                   [class.fa-arrow-down]="growthPercentage < 0"
                   [class.text-success]="growthPercentage >= 0"
                   [class.text-danger]="growthPercentage < 0"></i>
              </div>
              <div class="ms-4">
                <div class="fs-2hx fw-bold" [class.text-success]="growthPercentage >= 0" [class.text-danger]="growthPercentage < 0">
                  {{ growthPercentage >= 0 ? '+' : '' }}{{ growthPercentage }}%
                </div>
                <div class="text-muted fw-semibold">{{ 'companyProfile.membershipPlans.stats.vsLastMonth' | translate }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  @if (plans.length === 0) {
    <div class="card mb-9">
      <div class="card-body text-center py-15">
        <i class="fa fa-receipt fs-3x text-muted mb-5"></i>
        <h4 class="text-muted">{{ 'companyProfile.membershipPlans.noPlans' | translate }}</h4>
        <p class="text-muted mb-5">{{ 'companyProfile.membershipPlans.noPlanHint' | translate }}</p>
        <app-button
          [label]="'companyProfile.membershipPlans.createPlan' | translate"
          [variant]="'primary'"
          [iconClass]="'fa fa-plus'"
          [disabled]="needsSubscription()"
          (clicked)="openAddPlanModal()"
        />
      </div>
    </div>
  } @else {
    <div class="row g-6 g-xl-9 mb-9">
      @for (plan of plans; track plan.plan_id) {
        <div class="col-md-6 col-xl-4">
          <div class="card h-100 plan-card">
            <!-- Plan Header -->
            <div class="plan-header">
              <div class="plan-icon">
                <i class="fa fa-id-card"></i>
              </div>
              <!-- Member Count Badge -->
              <div class="member-badge" (click)="openViewMembersModal(plan)">
                <i class="fa fa-users me-1"></i>
                {{ plan.memberCount }} {{ plan.memberCount === 1 ? ('companyProfile.membershipPlans.member' | translate) : ('companyProfile.membershipPlans.members' | translate) }}
              </div>
            </div>

            <div class="card-body d-flex flex-column p-9">
              <!-- Coverage Badge -->
              <div class="mb-3">
                <span class="badge" [class.badge-primary]="plan.coverage === 'individual'" [class.badge-success]="plan.coverage === 'family'">
                  {{ plan.coverage | titlecase }}
                </span>
                @if (plan.max_children) {
                  <span class="badge badge-light-secondary ms-2">{{ 'companyProfile.membershipPlans.maxChildren' | translate:{ count: plan.max_children } }}</span>
                }
              </div>

              <!-- Plan Name -->
              <h3 class="card-title fw-bold fs-4 mb-3">{{ plan.name }}</h3>

              <!-- Plan Details -->
              <div class="mb-5">
                <div class="row g-3">
                  <!-- Left Column -->
                  <div class="col-6">
                    <!-- Price -->
                    <div class="d-flex align-items-start mb-3">
                      <i class="fa fa-tag text-primary me-2 fs-6 mt-1"></i>
                      <div>
                        <div class="fw-semibold fs-7">{{ formatPrice(plan.price_cents) }} kr</div>
                        <div class="text-muted fs-8">{{ 'companyProfile.membershipPlans.price' | translate }}</div>
                      </div>
                    </div>

                    <!-- Billing Cycle -->
                    <div class="d-flex align-items-start">
                      <i class="fa fa-sync text-info me-2 fs-6 mt-1"></i>
                      <div>
                        <div class="fw-semibold fs-7">{{ plan.billing_cycle | titlecase }}</div>
                        <div class="text-muted fs-8">{{ 'companyProfile.membershipPlans.billing' | translate }}</div>
                      </div>
                    </div>
                  </div>

                  <!-- Right Column -->
                  <div class="col-6">
                    <!-- Active Members -->
                    <div class="d-flex align-items-start mb-3">
                      <i class="fa fa-user-check text-success me-2 fs-6 mt-1"></i>
                      <div>
                        <div class="fw-semibold fs-7">{{ plan.activeMemberCount }}</div>
                        <div class="text-muted fs-8">{{ 'companyProfile.membershipPlans.active' | translate }}</div>
                      </div>
                    </div>

                    <!-- Created Date -->
                    <div class="d-flex align-items-start">
                      <i class="fa fa-calendar text-muted me-2 fs-6 mt-1"></i>
                      <div>
                        <div class="fw-semibold fs-7">{{ plan.created_at | date:'shortDate' }}</div>
                        <div class="text-muted fs-8">{{ 'companyProfile.membershipPlans.created' | translate }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="mt-auto d-flex gap-2">
                <div class="flex-grow-1">
                  <app-button
                    [label]="'companyProfile.membershipPlans.viewMembers' | translate"
                    [variant]="'outline'"
                    [iconClass]="'fa fa-users'"
                    [width]="'100%'"
                    (clicked)="openViewMembersModal(plan)"
                  />
                </div>
                <div class="flex-grow-1">
                  <app-button
                    [label]="'companyProfile.membershipPlans.edit' | translate"
                    [variant]="'primary'"
                    [iconClass]="'fa fa-edit'"
                    [width]="'100%'"
                    (clicked)="openEditPlanModal(plan)"
                  />
                </div>
                <app-button
                  [variant]="'danger'"
                  [iconClass]="'fa fa-trash'"
                  [width]="'48px'"
                  (clicked)="deletePlan(plan)"
                />
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  }
}
